/**
 * CentralHoshinPage — Cockpit de Execução Estratégica (Metas / Hoshin Kanri)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, RefreshCw, Target, X, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useDataScope } from '../hooks/useDataScope';
import { useHoshinDashboardData } from '../hooks/useHoshinDashboardData';
import { getDepartments } from '../services/organizationStructureService';
import { getMembersLight, type OrgMember } from '../services/organizationService';
import {
  createStrategicObjective,
  type CreateObjectiveParams,
} from '../services/hoshinService';
import type { RoleType } from '../services/scopeService';
import { HoshinExecutiveStrip } from '../components/hoshin/HoshinExecutiveStrip';
import { HoshinObjectivesTable, type HoshinSelection } from '../components/hoshin/HoshinObjectivesTable';
import { HoshinCockpitPanel } from '../components/hoshin/HoshinCockpitPanel';

function quarterToDate(year: number, q: 1 | 2 | 3 | 4): string {
  const ends = ['03-31', '06-30', '09-30', '12-31'];
  return `${year}-${ends[q - 1]}`;
}

const CURRENT_YEAR = new Date().getFullYear();

function CreateObjectiveModal({
  open,
  organizationId,
  userId,
  roleType,
  onClose,
  onCreated,
}: {
  open: boolean;
  organizationId: string;
  userId: string;
  roleType: RoleType;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fiscalYear, setFiscalYear] = useState(CURRENT_YEAR);
  const [quarter, setQuarter] = useState<1 | 2 | 3 | 4>(4);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setFiscalYear(CURRENT_YEAR);
    setQuarter(4);
    setError(null);
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Título obrigatório.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const params: CreateObjectiveParams = {
        organization_id: organizationId,
        created_by: userId,
        title: title.trim(),
        description: description.trim() || undefined,
        target_date: quarterToDate(fiscalYear, quarter),
      };
      await createStrategicObjective(params, roleType);
      onCreated();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao criar objetivo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Novo Objetivo Estratégico</span>
          <button type="button" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="space-y-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título do objetivo estratégico"
            className="w-full rounded border border-slate-200 px-2 py-1.5 text-[12px] dark:border-slate-700 dark:bg-slate-800"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Declaração estratégica (opcional)"
            rows={2}
            className="w-full resize-none rounded border border-slate-200 px-2 py-1.5 text-[11px] dark:border-slate-700 dark:bg-slate-800"
          />
          <div className="grid grid-cols-2 gap-2">
            <select value={fiscalYear} onChange={(e) => setFiscalYear(Number(e.target.value))} className="rounded border border-slate-200 px-2 py-1 text-[11px] dark:border-slate-700 dark:bg-slate-800">
              {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => <option key={y} value={y}>FY {y}</option>)}
            </select>
            <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value) as 1 | 2 | 3 | 4)} className="rounded border border-slate-200 px-2 py-1 text-[11px] dark:border-slate-700 dark:bg-slate-800">
              <option value={1}>Q1</option><option value={2}>Q2</option><option value={3}>Q3</option><option value={4}>Q4</option>
            </select>
          </div>
          {error && <p className="text-[10px] text-red-600">{error}</p>}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded border border-slate-200 px-3 py-1 text-[11px]">Cancelar</button>
          <button type="button" onClick={handleSave} disabled={saving} className="rounded bg-slate-800 px-3 py-1 text-[11px] text-white disabled:opacity-50">
            {saving ? 'Salvando…' : 'Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export const CentralHoshinPage: React.FC = () => {
  const { user } = useAuth();
  const organizationId = useOrganizationId();
  const { roleType } = useDataScope();

  const [members, setMembers] = useState<OrgMember[]>([]);
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [departmentMap, setDepartmentMap] = useState<Map<string, string>>(new Map());
  const [selection, setSelection] = useState<HoshinSelection | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const loadOrgMeta = useCallback(async () => {
    if (!organizationId) return;
    try {
      const [depts, mbrs] = await Promise.all([
        getDepartments(organizationId),
        getMembersLight(organizationId),
      ]);
      setDepartmentMap(new Map(depts.map((d) => [d.id, d.name])));
      setDepartments(depts.map((d) => ({ id: d.id, name: d.name })));
      setMembers(mbrs);
    } catch (err: unknown) {
      setFetchError(err instanceof Error ? err.message : 'Erro ao carregar estrutura organizacional.');
    }
  }, [organizationId]);

  useEffect(() => { void loadOrgMeta(); }, [loadOrgMeta]);

  const { data: dashboard, isLoading, refetch } = useHoshinDashboardData(
    organizationId,
    members,
    departmentMap,
  );

  const canCreateObjective = roleType === 'director';
  const selectedId = selection?.data.id ?? null;
  const goals = dashboard?.objectives.flatMap((objective) => objective.goals) ?? [];
  const goalsWithoutWork = goals.filter((goal) => goal.action_count === 0).length;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      {/* Header executivo */}
      <div className="shrink-0 border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Target size={14} className="shrink-0 text-slate-500" />
            <div>
              <h1 className="text-[13px] font-semibold leading-tight">Metas</h1>
              <p className="text-[11px] text-slate-500">Objetivo da diretoria desdobrado por setor e responsável</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[10px] text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
            >
              <RefreshCw size={11} />
              Atualizar
            </button>
            {canCreateObjective && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex items-center gap-1 rounded bg-slate-800 px-2 py-1 text-[10px] font-medium text-white hover:bg-slate-700"
              >
                <Plus size={11} />
                Objetivo
              </button>
            )}
          </div>
        </div>

        {dashboard && (
          <div className="mt-2">
            <HoshinExecutiveStrip
              objectives={dashboard.summary.totalObjectives}
              goals={goals.length}
              goalsWithoutWork={goalsWithoutWork}
              overdueActions={dashboard.summary.overdueActions}
            />
          </div>
        )}
      </div>

      {/* Corpo: tabela + x-matrix + cockpit */}
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto p-2">
          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-16 text-[11px] text-slate-500">
              <Loader2 size={14} className="animate-spin" />
              Carregando metas…
            </div>
          )}

          {!isLoading && fetchError && (
            <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">
              <XCircle size={14} className="shrink-0" />
              {fetchError}
            </div>
          )}

          {!isLoading && !fetchError && dashboard && (
            <HoshinObjectivesTable
              objectives={dashboard.objectives}
              selectedId={selectedId}
              onSelect={setSelection}
            />
          )}
        </div>

        <div className="hidden w-[340px] shrink-0 border-l border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 md:block">
          <HoshinCockpitPanel
            selection={selection}
            roleType={roleType}
            members={members}
            departments={departments}
            onRefresh={() => refetch()}
          />
        </div>
      </div>

      {organizationId && user?.id && (
        <CreateObjectiveModal
          open={createOpen}
          organizationId={organizationId}
          userId={user.id}
          roleType={roleType}
          onClose={() => setCreateOpen(false)}
          onCreated={() => refetch()}
        />
      )}
    </div>
  );
};
